/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import SessionAction from "store/session/SessionAction";
import HallAction from "store/hall/HallAction";
import {DEFAULT_HALL_ID} from "helpers/config";
import Loader from "components/Loader";
import {postUnsubscribe} from "helpers/actions";
import {withRouter} from "react-router-dom";

const UnsubscribeNewsletter = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const [isFloating, setIsFloating] = useState<boolean>(false);
    const [listId, setListId] = useState<number | null>(null);
    const hall = useTypedSelector(state => state.hallState?.hall) || null;
    const queryParams = new URLSearchParams(location.search);
    const filterParams: any = Object.fromEntries(queryParams.entries());

    const {
        register,
        reset,
        handleSubmit,
        setValue,
        formState: {errors},
    } = useForm();
    const onSubmit = data => {
        const {subject, ...rest} = data;
        if (subject === "") {
            data.subject = "Unsubcribe from newsletter";
        }
        setIsDisabled(true);
        // @ts-ignore
        return dispatch(postUnsubscribe(data)).then(response => {
            setIsDisabled(false);
            reset();
        });
    };
    const onError = data => {
        setIsDisabled(true);
    };

    useEffect(() => {
        if (filterParams.list_id) {
            setListId(filterParams.list_id);
        }
        console.log(filterParams);
    }, []);

    useEffect(() => {
        if (listId) {
            setValue("list_id", +listId);
        }
    }, [listId]);

    useEffect(() => {
        if (!hall) {
            dispatch(SessionAction.loginVisitor());
            dispatch(HallAction.fetchHall(DEFAULT_HALL_ID)).then(r => {});
        }
    }, []);

    if (!hall) {
        return <Loader />;
    }

    return (
        <div className={"UnsubscribeNewsletter w-full m-auto flex-wrap md:flex-nowrap md:space-x-6 relative"}>
            <div className="bg-white py-16 md:py-24 lg:py-32 shadow-lg rounded-2xl">
                <div className="max-w-7xl px-6 lg:pl-8 md:w-2/3 lg:pr-32">
                    <h2 className="max-w-3xl text-balance text-2xl font-semibold tracking-tight text-gray-900 md:text-4xl">
                        {t("Not interested in updates and discount vouchers?")}
                    </h2>
                    <h3 className="max-w-3xl text-balance text-2xl font-medium tracking-tight text-gray-700 md:text-3xl">
                        {t("You can unsubscribe from our ArboPark newsletter at any time.")}
                    </h3>
                    <form className="mt-10 max-w-md" autoComplete="off" name={"UnsubscribeNewsletterForm"} onSubmit={handleSubmit(onSubmit, onError)}>
                        <div className="flex flex-col md:flex-row gap-y-4 md:gap-x-4">
                            <input type={"hidden"} {...register("list_id")} id={"list_id"} />
                            <label htmlFor="email-address" className="sr-only">
                                {t("Email address")}
                            </label>
                            <input
                                id="email-address"
                                type="email"
                                required
                                placeholder={t("Enter your e-mail address")}
                                autoComplete="email"
                                {...register("email")}
                                className="min-w-0 flex-auto rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 md:text-sm/6"
                            />
                            <button
                                type="submit"
                                className="flex-none rounded-md bg-arbo-orange px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-arbo-orange/50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                disabled={isDisabled}
                                onClick={() => handleSubmit(onSubmit, onError)}
                            >
                                {t("Unsubscribe")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
            <div
                className="hidden md:flex image-container absolute md:left-2/3 lg:left-1/2 right-0 inset-y-0 overflow-hidden transform-gpu duration-300 ease-in-out"
                onMouseOver={() => setIsFloating(true)}
                onMouseOut={() => setIsFloating(false)}
                style={
                    !isFloating
                        ? {
                              clipPath: "polygon(30% 0%, 100% 0%, 100% 100%, 0% 100%, 15% 50%)",
                          }
                        : {
                              clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 30% 50%)",
                          }
                }
            >
                <div
                    className={"background-right w-full h-full bg-cover bg-[65%_center] lg:bg-center"}
                    style={{
                        backgroundImage: "url(https://www.arbopark.ch/wp-content/uploads/2024/05/kart_header-2560x922-c-center.jpg)",
                    }}
                />
            </div>
        </div>
    );
};

export default withRouter(UnsubscribeNewsletter);
